import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { AccountDeletionPreview } from '../response-models/account-deletion-preview.model';

/**
 * Account API Service
 *
 * Self-service account deletion against the Hub `/auth/account` endpoints
 * (`GET /auth/account/deletion-preview`, `DELETE /auth/account`).
 *
 * "Deletion" is deactivate-and-retain for FAA compliance: access is removed
 * and a one-time recovery code is issued, but compliance records are kept.
 * See agent-os/specs/2026-05-29-account-deletion/spec.md.
 *
 * Web counterpart of crop-swipe/lib/core/api/account_api.dart — keep the
 * surface in sync if either side gains a method.
 *
 * (Recovery is admin-mediated and not exposed here — no client recover call.)
 */
@Injectable({
  providedIn: 'root'
})
export class AccountApiService extends ApiService {
  constructor() {
    super('auth/account');
  }

  /**
   * What a deletion would entail for the current user (blockers + orgs
   * affected). Authenticated via the HTTP interceptor; no org header
   * needed (it's a `/auth` route).
   */
  async deletionPreview(): Promise<AccountDeletionPreview> {
    const preview = await firstValueFrom(
      this.request('GET', { path: 'deletion-preview' }, data => (isRecord(data) ? (data as unknown as AccountDeletionPreview) : null))
    );
    if (!preview) {
      throw new Error('Failed to load deletion preview');
    }
    return preview;
  }

  /**
   * Deletes (deactivates) the current account and returns the one-time
   * recovery code. The Hub re-validates preconditions and returns `409`
   * with code `ACCOUNT_DELETION_BLOCKED` (the preview in `detail`) if the
   * account isn't deletable; callers should handle that by reloading the preview.
   */
  async deleteAccount(): Promise<string> {
    const read = (data: unknown) => (isRecord(data) ? nullableString(data['recoveryCode']) : undefined) ?? '';
    return firstValueFrom(this.request('DELETE', {}, read));
  }
}
